import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/Header';
import BottomNavigation from '@/components/BottomNavigation';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Exercise, ExerciseType } from '@shared/schema';

import { getExerciseImage } from '@/lib/exerciseImages';

const ExerciseDetail: React.FC<{ exercise: Exercise }> = ({ exercise }) => {
  // Use the same exercise images as in the workout cards
  const exerciseImage = getExerciseImage(exercise.name);

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>{exercise.name}</CardTitle>
        <CardDescription>
          {exercise.exerciseType === ExerciseType.REPS ? (
            <>
              {exercise.initialSets} sets • {exercise.initialReps} reps
            </>
          ) : (
            <>
              {exercise.initialSets} sets • {exercise.initialDuration} seconds
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative w-full h-40 rounded-lg overflow-hidden mb-4">
          <img 
            src={exerciseImage}
            alt={`${exercise.name} demonstration`}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If the image fails to load, use a fallback image
              e.currentTarget.src = "/pushup.png";
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
            {exercise.name}
          </div>
        </div>
        
        <h4 className="font-medium mb-2">Instructions</h4>
        <p className="text-sm text-gray-600 mb-4">{exercise.description}</p>
        
        <h4 className="font-medium mb-2">Recommendation</h4>
        <p className="text-sm text-gray-600">
          Frequency: {exercise.frequency}
        </p>
      </CardContent>
    </Card>
  );
};

const Exercises: React.FC = () => {
  const { data: exercises, isLoading } = useQuery<Exercise[]>({
    queryKey: ['/api/exercises'],
  });

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white shadow-lg relative">
      <Header title="Exercises" subtitle="Exercise library and instructions" />
      
      <div className="p-4 overflow-y-auto pb-20" style={{ maxHeight: 'calc(100vh - 72px)' }}>
        <h2 className="font-heading text-xl font-bold mb-4">Exercise Library</h2>
        
        {isLoading ? (
          <p>Loading exercises...</p>
        ) : exercises && exercises.length > 0 ? (
          exercises.map(exercise => (
            <ExerciseDetail key={exercise.id} exercise={exercise} />
          ))
        ) : (
          <p>No exercises found</p>
        )}
      </div>
      
      <BottomNavigation />
    </div>
  );
};

export default Exercises;
